import { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Upload, AlertCircle, CheckCircle, FileText, TrendingUp, AlertTriangle, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableItemSelect } from '@/components/ui/searchable-item-select';
import { hasPermission, isAdmin } from '@/utils/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerScheduleView = () => {
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [dailyDemands, setDailyDemands] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [holidays, setHolidays] = useState([]);
  const [showHolidayWarning, setShowHolidayWarning] = useState(false);
  const [holidayWarnings, setHolidayWarnings] = useState([]);
  const [pendingScheduleData, setPendingScheduleData] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, schedule: null });
  
  const [newSchedule, setNewSchedule] = useState({
    customer_id: '',
    item_id: '',
    start_date: '',
    end_date: '',
    qty: ''
  });

  const [demandSummary, setDemandSummary] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedCustomer) {
      fetchCustomerHolidays(selectedCustomer);
      fetchCustomerSchedules(selectedCustomer);
      fetchCustomerDemands(selectedCustomer);
    }
  }, [selectedCustomer]);

  const fetchData = async () => {
    try {
      const [custRes, itemRes] = await Promise.all([
        axios.get(`${API}/customers`),
        axios.get(`${API}/items`)
      ]);
      setCustomers(custRes.data);
      setItems(itemRes.data.filter(i => i.category === 'FG')); // Only FG items
      
      if (custRes.data.length > 0) {
        setSelectedCustomer(custRes.data[0].id);
        setNewSchedule(prev => ({ ...prev, customer_id: custRes.data[0].id }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    }
  };

  const fetchCustomerHolidays = async (customerId) => {
    try {
      const response = await axios.get(`${API}/customer-calendar/${customerId}`);
      setHolidays(response.data.filter(h => h.is_holiday));
    } catch (error) {
      console.error('Error fetching holidays:', error);
    }
  };

  const fetchCustomerSchedules = async (customerId) => {
    try {
      const response = await axios.get(`${API}/schedule/monthly`);
      // Handle paginated response format
      const scheduleData = response.data.data || response.data;
      const customerSchedules = Array.isArray(scheduleData) ? scheduleData.filter(s => s.customer_id === customerId) : [];
      setSchedules(customerSchedules);
    } catch (error) {
      console.error('Error fetching schedules:', error);
      setSchedules([]);
    }
  };

  const fetchCustomerDemands = async (customerId) => {
    try {
      const response = await axios.get(`${API}/schedule/daily`);
      // Handle paginated response format
      const demandData = response.data.data || response.data;
      const customerDemands = Array.isArray(demandData) ? demandData.filter(d => d.customer_id === customerId) : [];
      setDailyDemands(customerDemands.sort((a, b) => new Date(a.demand_date) - new Date(b.demand_date)));
    } catch (error) {
      console.error('Error fetching demands:', error);
      // If endpoint not found, set empty array
      setDailyDemands([]);
    }
  };

  const calculateWorkingDays = (startDate, endDate, excludeHolidays) => {
    const holidaySet = new Set(excludeHolidays.map(h => h.date));
    let workingDays = 0;
    let current = new Date(startDate);
    const end = new Date(endDate);
    
    while (current <= end) {
      const dateStr = current.toISOString().split('T')[0];
      const dayOfWeek = current.getDay();
      
      // Skip if holiday or weekend
      if (!holidaySet.has(dateStr) && dayOfWeek !== 0 && dayOfWeek !== 6) {
        workingDays++;
      }
      current.setDate(current.getDate() + 1);
    }
    
    return workingDays;
  };

  const handleScheduleCreate = async () => {
    console.log('=== CREATE SCHEDULE CLICKED ===');
    console.log('Form data:', newSchedule);
    
    if (!newSchedule.customer_id || !newSchedule.item_id || !newSchedule.start_date || 
        !newSchedule.end_date || !newSchedule.qty) {
      console.error('Validation failed - missing fields:', {
        customer_id: newSchedule.customer_id,
        item_id: newSchedule.item_id,
        start_date: newSchedule.start_date,
        end_date: newSchedule.end_date,
        qty: newSchedule.qty
      });
      toast.error('All fields are required');
      return;
    }

    if (parseFloat(newSchedule.qty) <= 0) {
      console.error('Validation failed - invalid quantity:', newSchedule.qty);
      toast.error('Quantity must be greater than 0');
      return;
    }
    
    console.log('Validation passed, creating schedule...');

    try {
      // Create schedule via bulk upload API
      const scheduleData = [{
        customer_id: newSchedule.customer_id,
        item_id: newSchedule.item_id,
        month: new Date(newSchedule.start_date).toISOString().slice(0, 7),
        start_date: newSchedule.start_date,
        end_date: newSchedule.end_date,
        qty: parseFloat(newSchedule.qty)
      }];

      const response = await axios.post(`${API}/schedule/upload`, scheduleData);
      
      // Check if response contains holiday warnings
      if (response.data.status === 'warning' && response.data.requires_confirmation) {
        // Show holiday warning dialog
        setHolidayWarnings(response.data.warnings);
        setShowHolidayWarning(true);
        setPendingScheduleData(scheduleData);
        return;
      }
      
      toast.success(`Schedule created! ${response.data.daily_created} daily demands generated`);
      
      // Calculate summary
      const workingDays = calculateWorkingDays(
        newSchedule.start_date,
        newSchedule.end_date,
        holidays.filter(h => 
          h.date >= newSchedule.start_date && h.date <= newSchedule.end_date
        )
      );
      
      const dailyQty = workingDays > 0 ? parseFloat(newSchedule.qty) / workingDays : 0;
      
      setDemandSummary({
        totalQty: parseFloat(newSchedule.qty),
        workingDays: workingDays,
        dailyQty: dailyQty.toFixed(2),
        holidaysExcluded: holidays.filter(h => 
          h.date >= newSchedule.start_date && h.date <= newSchedule.end_date
        ).length
      });
      
      // Reset form
      setNewSchedule({
        customer_id: selectedCustomer,
        item_id: '',
        start_date: '',
        end_date: '',
        qty: ''
      });
      
      // Refresh data
      fetchCustomerSchedules(selectedCustomer);
      fetchCustomerDemands(selectedCustomer);
      
    } catch (error) {
      console.error('Error creating schedule:', error);
      toast.error(error.response?.data?.detail || 'Failed to create schedule');
    }
  };

  const handleConfirmWithHolidays = async () => {
    try {
      const response = await axios.post(`${API}/schedule/upload-confirmed`, pendingScheduleData);
      
      toast.success(`Schedule created! ${response.data.daily_created} daily demands generated for working days only`);
      
      // Calculate summary
      const workingDays = calculateWorkingDays(
        newSchedule.start_date,
        newSchedule.end_date,
        holidays.filter(h => 
          h.date >= newSchedule.start_date && h.date <= newSchedule.end_date
        )
      );
      
      const dailyQty = workingDays > 0 ? parseFloat(newSchedule.qty) / workingDays : 0;
      
      setDemandSummary({
        totalQty: parseFloat(newSchedule.qty),
        workingDays: workingDays,
        dailyQty: dailyQty.toFixed(2),
        holidaysExcluded: holidays.filter(h => 
          h.date >= newSchedule.start_date && h.date <= newSchedule.end_date
        ).length
      });
      
      // Reset form
      setNewSchedule({
        customer_id: selectedCustomer,
        item_id: '',
        start_date: '',
        end_date: '',
        qty: ''
      });
      
      // Close dialog
      setShowHolidayWarning(false);
      setHolidayWarnings([]);
      setPendingScheduleData(null);
      
      // Refresh data
      if (selectedCustomer) {
        fetchCustomerSchedules(selectedCustomer);
        fetchCustomerDemands(selectedCustomer);
      }
    } catch (error) {
      console.error('Error confirming schedule with holidays:', error);
      toast.error(error.response?.data?.detail || 'Failed to create schedule');
    }
  };

  const handleCancelHolidayWarning = () => {
    setShowHolidayWarning(false);
    setHolidayWarnings([]);
    setPendingScheduleData(null);
  };

  const getCustomerName = (id) => customers.find(c => c.id === id)?.name || 'N/A';
  const getItemName = (id) => {
    const item = items.find(i => i.id === id);
    return item ? `${item.part_number} - ${item.description}` : 'N/A';
  };

  const getItemDescription = (id) => {
    const item = items.find(i => i.id === id);
    return item ? item.description : '';
  };

  // Get FG items filtered by selected customer
  const getFilteredFgItems = () => {
    if (!newSchedule.customer_id) {
      return items; // Show all FG items if no customer selected
    }
    // Filter FG items that belong to the selected customer or have no customer assigned
    return items.filter(item => 
      item.customer_id === newSchedule.customer_id || !item.customer_id
    );
  };

  const handleDeleteSchedule = async () => {
    if (!deleteDialog.schedule) return;
    
    try {
      await axios.delete(`${API}/schedule/${deleteDialog.schedule.id}`);
      toast.success('Schedule and related daily demands deleted successfully');
      setDeleteDialog({ open: false, schedule: null });
      // Refresh data
      if (selectedCustomer) {
        fetchCustomerSchedules(selectedCustomer);
        fetchCustomerDemands(selectedCustomer);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to delete schedule';
      toast.error(errorMsg);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-lg border border-purple-200">
        <div className="flex items-center gap-3 mb-2">
          <Calendar className="w-6 h-6 text-purple-600" />
          <h2 className="text-2xl font-bold text-slate-900">Customer Schedule & Demand Planning</h2>
        </div>
        <p className="text-slate-600">
          Upload monthly schedules for customers. Daily demands are automatically split across working days, excluding customer-specific holidays.
        </p>
      </div>

      {/* Create Schedule Form with Customer Selector */}
      {hasPermission('schedule', 'create') ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Create Monthly Schedule
            </CardTitle>
            <CardDescription>
              Select a customer and add a monthly schedule. Daily demands are auto-split across working days.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
              <div>
                <Label>Customer *</Label>
                <Select value={selectedCustomer} onValueChange={(val) => {
                  setSelectedCustomer(val);
                  setNewSchedule(prev => ({ ...prev, customer_id: val, item_id: '' }));
                }}>
                  <SelectTrigger data-testid="schedule-customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Finished Good Item *</Label>
                <SearchableItemSelect
                  items={getFilteredFgItems()}
                  value={newSchedule.item_id}
                  onValueChange={(value) => setNewSchedule({ ...newSchedule, item_id: value === 'all' ? '' : value })}
                  placeholder="Select FG item"
                  showCategory={false}
                  dataTestId="schedule-item-select"
                />
                {newSchedule.customer_id && getFilteredFgItems().length === 0 && (
                  <p className="text-xs text-orange-600 mt-1">
                    No FG items assigned to this customer.
                  </p>
                )}
              </div>

              <div>
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={newSchedule.start_date}
                  onChange={(e) => setNewSchedule({ ...newSchedule, start_date: e.target.value })}
                />
              </div>

              <div>
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={newSchedule.end_date}
                  onChange={(e) => setNewSchedule({ ...newSchedule, end_date: e.target.value })}
                />
              </div>

              <div>
                <Label>Total Quantity *</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="e.g., 1000"
                  value={newSchedule.qty}
                  onChange={(e) => setNewSchedule({ ...newSchedule, qty: e.target.value })}
                />
              </div>

              <div className="flex items-end">
                <Button onClick={handleScheduleCreate} className="w-full" data-testid="create-schedule-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Create Schedule
                </Button>
              </div>
            </div>

            {/* Holiday Info - inline */}
            {selectedCustomer && holidays.length > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-start gap-3 mb-4">
                <AlertCircle className="w-4 h-4 text-orange-600 mt-0.5 shrink-0" />
                <p className="text-sm text-orange-800">
                  <span className="font-medium">{holidays.length} holiday{holidays.length > 1 ? 's' : ''}</span> configured for {getCustomerName(selectedCustomer)}. Daily demands will automatically exclude these dates.
                </p>
              </div>
            )}

            {/* Demand Summary */}
            {demandSummary && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-900">Demand Split Successfully!</p>
                    <div className="grid grid-cols-4 gap-4 mt-2 text-sm text-green-700">
                      <div>
                        <span className="font-medium">Total Qty:</span> {demandSummary.totalQty}
                      </div>
                      <div>
                        <span className="font-medium">Working Days:</span> {demandSummary.workingDays}
                      </div>
                      <div>
                        <span className="font-medium">Daily Qty:</span> {demandSummary.dailyQty}
                      </div>
                      <div>
                        <span className="font-medium">Holidays Excluded:</span> {demandSummary.holidaysExcluded}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Select Customer</CardTitle>
            <CardDescription>Choose a customer to view schedules and demands</CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={selectedCustomer} onValueChange={(val) => {
              setSelectedCustomer(val);
              setNewSchedule(prev => ({ ...prev, customer_id: val }));
            }}>
              <SelectTrigger className="w-full" data-testid="schedule-customer-select">
                <SelectValue placeholder="Select customer" />
              </SelectTrigger>
              <SelectContent>
                {customers.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {selectedCustomer && (
        <>
          {/* Monthly Schedules */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Monthly Schedules
              </CardTitle>
              <CardDescription>
                All schedules for {getCustomerName(selectedCustomer)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {schedules.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No schedules found. Create one above to get started.
                </div>
              ) : (
                <div className="overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Part Number</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Period</TableHead>
                        <TableHead>Start Date</TableHead>
                        <TableHead>End Date</TableHead>
                        <TableHead className="text-right">Quantity</TableHead>
                        {isAdmin() && <TableHead className="text-center">Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {schedules.map((schedule) => {
                        const item = items.find(i => i.id === schedule.item_id);
                        return (
                          <TableRow key={schedule.id}>
                            <TableCell className="font-mono text-sm">
                              {item?.part_number || 'N/A'}
                            </TableCell>
                            <TableCell>
                              {item?.description || 'N/A'}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{schedule.month}</Badge>
                            </TableCell>
                            <TableCell>{schedule.start_date}</TableCell>
                            <TableCell>{schedule.end_date}</TableCell>
                            <TableCell className="text-right font-medium">
                              {schedule.qty}
                            </TableCell>
                            {isAdmin() && (
                              <TableCell className="text-center">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 hover:bg-red-50"
                                  onClick={() => setDeleteDialog({ open: true, schedule })}
                                  title="Delete schedule"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Daily Demands */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Daily Demands (Split)
              </CardTitle>
              <CardDescription>
                Daily demands generated from schedules, excluding holidays
              </CardDescription>
            </CardHeader>
            <CardContent>
              {dailyDemands.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No daily demands found. Create a schedule to generate demands.
                </div>
              ) : (
                <div className="overflow-auto max-h-96">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Day</TableHead>
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">Quantity</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dailyDemands.map((demand) => {
                        const date = new Date(demand.demand_date);
                        const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
                        
                        return (
                          <TableRow key={demand.id}>
                            <TableCell className="font-medium">{demand.demand_date}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{dayName}</Badge>
                            </TableCell>
                            <TableCell>{getItemName(demand.item_id)}</TableCell>
                            <TableCell className="text-right">{demand.qty}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Holiday Warning Dialog */}
      <Dialog open={showHolidayWarning} onOpenChange={setShowHolidayWarning}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="w-6 h-6" />
              Holiday Detected in Schedule
            </DialogTitle>
            <DialogDescription>
              Some dates in your schedule range include holidays. The system will exclude holiday dates and distribute quantity only across working days.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {holidayWarnings.map((warning, index) => (
              <div key={index} className="border border-orange-200 bg-orange-50 p-4 rounded-lg">
                <div className="font-semibold text-slate-800 mb-2">
                  {warning.customer} - {warning.item}
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-slate-600">Date Range:</span>
                    <span className="ml-2 font-medium">{warning.start_date} to {warning.end_date}</span>
                  </div>
                  <div>
                    <span className="text-slate-600">Total Quantity:</span>
                    <span className="ml-2 font-medium">{warning.qty} units</span>
                  </div>
                  <div>
                    <span className="text-slate-600">Total Days:</span>
                    <span className="ml-2 font-medium">{warning.total_days} days</span>
                  </div>
                  <div>
                    <span className="text-slate-600">Working Days:</span>
                    <span className="ml-2 font-medium text-green-600">{warning.working_days} days</span>
                  </div>
                  <div>
                    <span className="text-slate-600">Holiday Days:</span>
                    <span className="ml-2 font-medium text-red-600">{warning.holiday_days} days</span>
                  </div>
                  <div>
                    <span className="text-slate-600">Qty per Working Day:</span>
                    <span className="ml-2 font-medium text-blue-600">{warning.qty_per_working_day} units/day</span>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-orange-200">
                  <span className="text-slate-600 text-sm">Holidays to be excluded:</span>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {warning.holidays.map((holiday, idx) => (
                      <span key={idx} className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                        {holiday}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg mt-4">
            <p className="text-sm text-blue-800">
              ℹ️ <strong>Note:</strong> The schedule will be created, but daily demands will only be generated for working days. Holiday dates will be automatically excluded.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCancelHolidayWarning}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmWithHolidays}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Proceed with Working Days Only
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Schedule Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, schedule: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Schedule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this schedule? This will also delete all related daily demands.
              {deleteDialog.schedule && (
                <div className="mt-2 p-2 bg-slate-100 rounded text-sm">
                  <strong>Item:</strong> {items.find(i => i.id === deleteDialog.schedule.item_id)?.part_number || 'N/A'}<br />
                  <strong>Period:</strong> {deleteDialog.schedule.month}<br />
                  <strong>Quantity:</strong> {deleteDialog.schedule.qty}
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSchedule}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CustomerScheduleView;
